import type { Agent, AgentEvent, AgentMessage, AgentState, AgentTool } from "@earendil-works/pi-agent-core";
import type { ImageContent, Model, ThinkingLevel } from "@earendil-works/pi-ai";
import {
	fingerprintText,
	loadAttachmentCache,
	saveAttachmentEntry,
	type CachedAttachmentEntry,
} from "./attachment-cache.js";

type Listener = (event: AgentEvent, signal: AbortSignal) => Promise<void> | void;

interface ServerFrame {
	type: string;
	[k: string]: unknown;
}

/**
 * Adapter that exposes a remote pi-coding-agent session through the same
 * surface ChatPanel/AgentInterface uses for a local pi `Agent`.
 *
 * It mirrors agent state (messages, isStreaming, model, tools) by listening
 * to events streamed over WebSocket from the backend, and forwards user
 * prompts back to the server. The class implements only the methods/fields
 * AgentInterface actually touches; everything else throws or no-ops.
 */
export class PiTaskAgent {
	streamFn: unknown = () => {
		throw new Error("PiTaskAgent.streamFn invoked locally — server drives the loop.");
	};
	getApiKey?: (provider: string) => Promise<string | undefined> | string | undefined = () => "remote";
	steeringMode: "all" | "one-at-a-time" = "all";
	followUpMode: "all" | "one-at-a-time" = "all";
	sessionId?: string;
	transport: "sse" = "sse";
	toolExecution: "sequential" | "parallel" = "sequential";

	private _state: MutableState;
	private _listeners = new Set<Listener>();
	private _abortController = new AbortController();
	/** Counter of replayed messages this connection — used to match cached
	 *  attachment entries by their original `index` position. */
	private _replayIndex = 0;
	/** Cached `user-with-attachments` shapes for this task, loaded once on
	 *  construct so replay can restore PDF/image tiles after reopen. */
	private _attachmentCache: CachedAttachmentEntry[];

	constructor(
		readonly taskId: string,
		private ws: WebSocket,
		fallbackModel: Model<any>,
		/** Uploads one attachment into the task workdir and resolves to its
		 *  workdir-relative path. Supplied by the tab (which knows the
		 *  scope-aware API base). When present, document attachments are saved
		 *  as real files the agent can read/parse directly, instead of being
		 *  inlined as extracted text into the prompt. */
		private uploadAttachment?: (fileName: string, bytes: Blob) => Promise<string>,
	) {
		this._state = createState(fallbackModel);
		this._attachmentCache = loadAttachmentCache(taskId);
		ws.addEventListener("message", this.onMessage);
		ws.addEventListener("close", () => {
			// Mark as not streaming on disconnect so the UI re-enables input.
			if (this._state.isStreaming) {
				this._state.isStreaming = false;
				this.emit({ type: "agent_end", messages: [] });
			}
		});
	}

	get state(): AgentState {
		return this._state as unknown as AgentState;
	}

	get signal(): AbortSignal | undefined {
		return this._abortController.signal;
	}

	subscribe(listener: Listener): () => void {
		this._listeners.add(listener);
		return () => this._listeners.delete(listener);
	}

	async prompt(input: AgentMessage | AgentMessage[] | string, _images?: ImageContent[]): Promise<void> {
		const { text, images } = await extractPromptParts(input, this.uploadAttachment);
		// Locally append the user's message to state.messages BEFORE the
		// network round-trip so:
		//  (a) the user-with-attachments shape (PDF tile, image preview etc.)
		//      survives — pi-web-ui's <user-message> only renders attachment
		//      tiles when role === "user-with-attachments"; the server-side
		//      agent flattens attachments to inlined text, which would echo
		//      back as a plain `user` message and lose the tiles.
		//  (b) the user sees their bubble immediately even before any agent
		//      events come back over WS.
		// We then filter the server's user-role message_start/message_end
		// echoes in applyAgentEvent so we don't get duplicates.
		const localMsg = toLocalUserMessage(input);
		if (localMsg) {
			const idx = this._state.messages.length;
			this._state.messages = [...this._state.messages, localMsg];
			this.emit({ type: "message_end", message: localMsg } as AgentEvent);
			// Persist the attachment-shaped message so a future reopen can
			// restore the PDF/image tile. The server only stores the flattened
			// (inlined-text) version, so without this the reopened task shows
			// a wall of extracted text instead of the original tile.
			if ((localMsg as { role?: string }).role === "user-with-attachments") {
				const entry: CachedAttachmentEntry = {
					index: idx,
					textPrefix: fingerprintText(localMsg),
					message: localMsg,
				};
				saveAttachmentEntry(this.taskId, entry);
				// Keep the in-memory cache in sync so a same-session replay
				// (e.g. WS reconnect without page reload) also restores tiles.
				this._attachmentCache = [
					...this._attachmentCache.filter((c) => c.index !== idx),
					entry,
				];
			}
		}
		console.log("[PiTaskAgent] prompt", {
			taskId: this.taskId,
			len: text.length,
			images: images.length,
			ws: this.ws.readyState,
		});
		this.send({ type: "prompt", text, images });
	}

	async continue(): Promise<void> {
		// Continuation is owned by the server-side AgentSession; no-op here.
	}

	abort(): void {
		this.send({ type: "abort" });
		this._abortController.abort();
		this._abortController = new AbortController();
	}

	steer(_message: AgentMessage): void {
		// Steering arrives as additional prompts in our wire format.
	}
	followUp(_message: AgentMessage): void {}
	clearSteeringQueue(): void {}
	clearFollowUpQueue(): void {}
	clearAllQueues(): void {}
	hasQueuedMessages(): boolean {
		return false;
	}
	async waitForIdle(): Promise<void> {
		// Resolve when not streaming.
		if (!this._state.isStreaming) return;
		await new Promise<void>((resolve) => {
			const off = this.subscribe((e) => {
				if (e.type === "agent_end") {
					off();
					resolve();
				}
			});
		});
	}
	reset(): void {
		this._state.messages = [];
	}

	private onMessage = (e: MessageEvent) => {
		let frame: ServerFrame;
		try {
			frame = JSON.parse(e.data as string) as ServerFrame;
		} catch {
			return;
		}
		if (frame.type === "agent") {
			const ev = frame.event as { type?: string };
			console.log("[PiTaskAgent] agent event", ev?.type);
			this.applyAgentEvent(frame.event as AgentEvent | { type: "message_replay"; message: AgentMessage });
		} else if (frame.type === "task_meta" || frame.type === "welcome") {
			// nothing to do for the agent surface
		}
	};

	private applyAgentEvent(event: AgentEvent | { type: "message_replay"; message: AgentMessage }) {
		// Drop the server's echo of user-role message_start/message_end. We
		// already appended the user message locally in prompt(); accepting the
		// echo here would duplicate the bubble, AND the echoed message has its
		// attachments flattened into inlined text (so it would replace our nice
		// PDF/image tile with a wall of extracted text).
		// message_replay is a different code path (used on reconnect to
		// reconstruct history) and stays unfiltered.
		if (event.type === "message_start" || event.type === "message_end") {
			const role = (event as { message?: { role?: string } }).message?.role;
			if (role === "user" || role === "user-with-attachments") {
				return;
			}
		}
		switch (event.type) {
			case "message_replay": {
				const m = (event as { message: AgentMessage }).message;
				const idx = this._replayIndex++;
				// Restore PDF/image tiles for `user-with-attachments` messages
				// that we cached at original send time. The server replay sends
				// the flattened (inlined extracted text + base64 images) shape;
				// we swap it back to the tile-rendering shape if both the
				// position and the leading text agree.
				const cached = this._attachmentCache.find((c) => c.index === idx);
				const role = (m as { role?: string }).role;
				if (cached && (role === "user" || role === "user-with-attachments")) {
					const replayedText = fingerprintText(m, cached.textPrefix.length);
					if (replayedText.startsWith(cached.textPrefix)) {
						this._state.messages = [...this._state.messages, cached.message];
						this.emit({ type: "message_end", message: cached.message } as AgentEvent);
						return;
					}
				}
				this._state.messages = [...this._state.messages, m];
				this.emit({ type: "message_end", message: m } as AgentEvent);
				return;
			}
			case "agent_start":
				this._state.isStreaming = true;
				break;
			case "agent_end":
				this._state.isStreaming = false;
				this._state.streamingMessage = undefined;
				break;
			case "message_start":
				this._state.streamingMessage = (event as Extract<AgentEvent, { type: "message_start" }>).message;
				break;
			case "message_update":
				this._state.streamingMessage = (event as Extract<AgentEvent, { type: "message_update" }>).message;
				break;
			case "message_end": {
				const m = (event as Extract<AgentEvent, { type: "message_end" }>).message;
				this._state.messages = [...this._state.messages, m];
				this._state.streamingMessage = undefined;
				break;
			}
			default:
				break;
		}
		this.emit(event as AgentEvent);
	}

	private emit(event: AgentEvent) {
		for (const l of this._listeners) {
			try {
				const r = l(event, this._abortController.signal);
				if (r && typeof (r as Promise<void>).then === "function") void (r as Promise<void>);
			} catch (err) {
				console.warn("PiTaskAgent listener error", err);
			}
		}
	}

	private send(frame: { type: string; [k: string]: unknown }) {
		if (this.ws.readyState === WebSocket.OPEN) {
			this.ws.send(JSON.stringify(frame));
			return;
		}
		if (this.ws.readyState === WebSocket.CONNECTING) {
			// Defer until the socket opens so we don't drop the user's first
			// message after a fresh task is created.
			this.ws.addEventListener(
				"open",
				() => this.ws.send(JSON.stringify(frame)),
				{ once: true },
			);
			return;
		}
		console.warn("[PiTaskAgent] dropping frame, ws state =", this.ws.readyState, frame);
	}
}

interface MutableState {
	systemPrompt: string;
	model: Model<any>;
	thinkingLevel: ThinkingLevel;
	tools: AgentTool<any>[];
	messages: AgentMessage[];
	isStreaming: boolean;
	streamingMessage?: AgentMessage;
	pendingToolCalls: Set<string>;
	errorMessage?: string;
}

function createState(model: Model<any>): MutableState {
	return {
		systemPrompt: "",
		model,
		thinkingLevel: "medium",
		tools: [],
		messages: [],
		isStreaming: false,
		streamingMessage: undefined,
		pendingToolCalls: new Set(),
		errorMessage: undefined,
	};
}

/**
 * Extract LLM-ready text + image payloads from whatever AgentInterface hands us.
 *
 * Handles three shapes:
 *  - plain string (just text)
 *  - LLM message with content blocks (text/image)
 *  - `user-with-attachments` from pi-web-ui's MessageEditor (paperclip uploads):
 *    image attachments get pulled out as ImageContent entries; document
 *    attachments are UPLOADED into the task workdir (via `upload`) and only
 *    referenced by path in the prompt — the server-side agent has real file
 *    tools, so handing it the actual file beats inlining extracted text
 *    (which once cost a model 6k tokens re-typing a CSV it couldn't find on
 *    disk). Inlining remains the fallback when no uploader is supplied or the
 *    upload fails.
 */
interface SerializedImage {
	mimeType: string;
	data: string;
}

async function extractPromptParts(
	input: AgentMessage | AgentMessage[] | string,
	upload?: (fileName: string, bytes: Blob) => Promise<string>,
): Promise<{ text: string; images: SerializedImage[] }> {
	const arr: Array<AgentMessage | string> = Array.isArray(input) ? input : [input];
	const textParts: string[] = [];
	const images: SerializedImage[] = [];

	for (const m of arr) {
		if (typeof m === "string") {
			textParts.push(m);
			continue;
		}
		const role = (m as { role?: string }).role;
		const content = (m as { content?: unknown }).content;
		const attachments = (m as { attachments?: Array<{ type?: string; mimeType?: string; content?: string; extractedText?: string; fileName?: string }> }).attachments;

		if (typeof content === "string") {
			textParts.push(content);
		} else if (Array.isArray(content)) {
			for (const c of content) {
				const t = (c as { type?: string }).type;
				if (t === "text") textParts.push((c as { text?: string }).text ?? "");
				else if (t === "image") {
					const ic = c as { mimeType?: string; data?: string };
					if (ic.data && ic.mimeType) images.push({ mimeType: ic.mimeType, data: ic.data });
				}
			}
		}

		if (role === "user-with-attachments" && Array.isArray(attachments)) {
			for (const a of attachments) {
				if (a.type === "image" && a.content && a.mimeType) {
					images.push({ mimeType: a.mimeType, data: a.content });
				} else if (a.type === "document") {
					const name = a.fileName ?? "attachment.txt";
					const saved = upload ? await tryUploadDocument(upload, name, a) : undefined;
					if (saved) {
						textParts.push(
							`\n\n[Angehängte Datei "${name}" liegt im Arbeitsverzeichnis: ./${saved} — direkt mit den Datei-Tools lesen/verarbeiten.]`,
						);
					} else if (a.extractedText) {
						textParts.push(`\n\n[Document: ${name}]\n${a.extractedText}`);
					}
				}
			}
		}
	}

	return { text: textParts.join("\n").trim(), images };
}

/**
 * Upload one document attachment, preferring the original bytes (base64
 * `content`) over the text extraction so binary formats (PDF, xlsx) arrive
 * intact. Returns the workdir-relative path, or undefined on any failure so
 * the caller falls back to inlining.
 */
async function tryUploadDocument(
	upload: (fileName: string, bytes: Blob) => Promise<string>,
	name: string,
	a: { mimeType?: string; content?: string; extractedText?: string },
): Promise<string | undefined> {
	try {
		let blob: Blob;
		if (a.content) {
			const raw = atob(a.content);
			const bytes = new Uint8Array(raw.length);
			for (let i = 0; i < raw.length; i++) bytes[i] = raw.charCodeAt(i);
			blob = new Blob([bytes], { type: a.mimeType ?? "application/octet-stream" });
		} else if (a.extractedText) {
			blob = new Blob([a.extractedText], { type: "text/plain" });
		} else {
			return undefined;
		}
		return await upload(name, blob);
	} catch (err) {
		console.warn("[PiTaskAgent] attachment upload failed, inlining instead", err);
		return undefined;
	}
}

/**
 * Build the AgentMessage we want to display locally for the user's prompt.
 *
 * - String input → synthesize a `{ role: "user", content }` message.
 * - LLM-shaped user message → pass through.
 * - `user-with-attachments` → pass through (preserves attachment tiles).
 * - AgentMessage[] → take the first user-shaped entry (rare path; AgentInterface
 *   always sends a single message in our setup).
 */
function toLocalUserMessage(input: AgentMessage | AgentMessage[] | string): AgentMessage | undefined {
	const now = Date.now();
	if (typeof input === "string") {
		const trimmed = input.trim();
		if (!trimmed) return undefined;
		return { role: "user", content: input, timestamp: now } as unknown as AgentMessage;
	}
	const arr = Array.isArray(input) ? input : [input];
	const first = arr[0];
	if (!first) return undefined;
	return first;
}

// Cast helper so callers can hand this to ChatPanel which expects an Agent.
export function asAgent(p: PiTaskAgent): Agent {
	return p as unknown as Agent;
}
